<script setup lang="ts">
    import { reactive, computed, watch } from 'vue';
    import { getFreeTextAnswers, buildQueryString, type DateRange } from '../SurveyStatistics.access';
    import { QuestionType, type Question, type QuestionFilter, type SurveyFreeTextRespondent } from '../../SurveysTypes.d';
    import { formatDateTime } from '@shared/utils/formatDateTime';

    const props = defineProps<{
        surveyId: string;
        question: Question;
        dateRange: DateRange;
        questionFilters: QuestionFilter[];
        autoFilters: QuestionFilter[];
        selectedCultures: string[];
    }>();

    const emit = defineEmits<{
        toggleFilterValue: [value: string];
    }>();

    const FREE_TEXT_RESPONSES_PER_PAGE = 50;
    const GUID_EMPTY = '00000000-0000-0000-0000-000000000000';

    const state = reactive<{
        answers: SurveyFreeTextRespondent[];
        skip: number;
        isLoading: boolean;
        expanded: Set<string>;
        total: number | null;
    }>({
        answers: [],
        skip: 0,
        isLoading: false,
        expanded: new Set(),
        total: null,
    });

    const qs = computed(() => buildQueryString(props.dateRange, props.questionFilters, props.selectedCultures));

    watch(qs, () => {
        state.answers = [];
        state.skip = 0;
        state.expanded.clear();
        state.total = null;
    });

    async function loadAnswers() {
        if (state.isLoading) return;

        state.isLoading = true;
        try {
            const result = await getFreeTextAnswers(props.surveyId, props.question.QuestionId, qs.value, state.skip, FREE_TEXT_RESPONSES_PER_PAGE);
            state.answers.push(...result.Results);
            state.skip += FREE_TEXT_RESPONSES_PER_PAGE;
            state.total = result.Count;
        } finally {
            state.isLoading = false;
        }
    }

    function rowKey(respondent: SurveyFreeTextRespondent, index: number): string {
        return `${respondent.ContactId}:${index}`;
    }

    function isExpanded(key: string): boolean {
        return state.expanded.has(key);
    }

    function toggleExpanded(key: string) {
        if (state.expanded.has(key)) {
            state.expanded.delete(key);
        } else {
            state.expanded.add(key);
        }
    }

    function responseRoute(surveyResponseId: string) {
        return { name: 'SurveyStatisticsResponse', params: { surveyId: props.surveyId, responseId: surveyResponseId } };
    }

    const isHiddenQuestion = computed(() => props.question.Type === QuestionType.Hidden);

    function isValueInAutoFilter(value: string): boolean {
        return props.autoFilters.some((f) => f.QuestionId === props.question.QuestionId && f.Value === value);
    }

    function isValueInFilter(value: string): boolean {
        return props.questionFilters.some((f) => f.QuestionId === props.question.QuestionId && f.Value === value);
    }

    function respondentName(respondent: SurveyFreeTextRespondent): string {
        return respondent.ContactId !== GUID_EMPTY
            ? [respondent.FirstName, respondent.LastName].filter(Boolean).join(' ') || $Resources.NoContactNamePlaceholder
            : respondent.FirstName;
    }
</script>

<template>
    <div v-if="state.answers.length > 0" class="custom-scrollbar mx-h-400 m-sm-b">
        <div class="table-responsive">
            <table class="table table-hover m-0-b">
                <thead>
                    <tr>
                        <th>{{ $Resources.FreeTextAnswer }}</th>
                        <th>{{ $Resources.FreeTextFullResponse }}</th>
                        <th>{{ $Resources.FreeTextRespondent }}</th>
                        <th class="autofit"></th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(respondent, index) in state.answers" :key="rowKey(respondent, index)">
                        <tr>
                            <td>
                                <span class="d-flex flex-wrap gap-xs align-items-baseline">
                                    {{ respondent.FreeTextAnswers[0].Freetext }}
                                    <small class="fw-600" v-tooltip.top="$Resources.ResponseCompleted">
                                        ({{ formatDateTime(respondent.FreeTextAnswers[0].Created) }})
                                    </small>
                                    <button
                                        v-if="respondent.FreeTextAnswers.length > 1"
                                        type="button"
                                        class="btn btn-default btn-xs hidden-print"
                                        @click="toggleExpanded(rowKey(respondent, index))">
                                        {{ $Resources.FreeTextAnswersShowEarlierAnswersLabel }} ({{ respondent.FreeTextAnswers.length - 1 }})
                                    </button>
                                </span>
                            </td>
                            <td class="text-nowrap">
                                <router-link :to="responseRoute(respondent.FreeTextAnswers[0].SurveyResponseId)">
                                    {{ $Resources.ShowSurveyAnswersLabel }}
                                </router-link>
                            </td>
                            <td class="text-nowrap">{{ respondentName(respondent) }}</td>
                            <td class="autofit">
                                <button
                                    type="button"
                                    class="btn btn-default btn-xs hidden-print"
                                    v-if="isHiddenQuestion && !isValueInAutoFilter(respondent.FreeTextAnswers[0].Freetext)"
                                    @click.prevent="emit('toggleFilterValue', respondent.FreeTextAnswers[0].Freetext)">
                                    <template v-if="!isValueInFilter(respondent.FreeTextAnswers[0].Freetext)">
                                        <span class="glyphicon glyphicon-filter"></span>
                                        {{ $Resources.SurveyAddQuestionOptionFilter }}
                                    </template>
                                    <template v-else>
                                        <span class="glyphicon glyphicon-remove"></span>
                                        {{ $Resources.SurveyRemoveQuestionOptionFilter }}
                                    </template>
                                </button>
                            </td>
                        </tr>
                        <template v-if="isExpanded(rowKey(respondent, index))">
                            <tr v-for="answer in respondent.FreeTextAnswers.slice(1)" :key="answer.SurveyResponseId">
                                <td class="p-lg-l">
                                    <span class="d-flex flex-wrap gap-xs align-items-baseline">
                                        {{ answer.Freetext }}
                                        <small class="fw-600" v-tooltip.top="$Resources.ResponseCompleted">
                                            ({{ formatDateTime(answer.Created) }})
                                        </small>
                                    </span>
                                </td>
                                <td class="text-nowrap">
                                    <router-link :to="responseRoute(answer.SurveyResponseId)">
                                        {{ $Resources.ShowSurveyAnswersLabel }}
                                    </router-link>
                                </td>
                                <td class="text-nowrap">{{ respondentName(respondent) }}</td>
                                <td class="autofit">
                                    <button
                                        type="button"
                                        class="btn btn-default btn-xs hidden-print"
                                        v-if="isHiddenQuestion && !isValueInAutoFilter(respondent.FreeTextAnswers[0].Freetext)"
                                        @click.prevent="emit('toggleFilterValue', respondent.FreeTextAnswers[0].Freetext)">
                                        <template v-if="!isValueInFilter(respondent.FreeTextAnswers[0].Freetext)">
                                            <span class="glyphicon glyphicon-filter"></span>
                                            {{ $Resources.SurveyAddQuestionOptionFilter }}
                                        </template>
                                        <template v-else>
                                            <span class="glyphicon glyphicon-remove"></span>
                                            {{ $Resources.SurveyRemoveQuestionOptionFilter }}
                                        </template>
                                    </button>
                                </td>
                            </tr>
                        </template>
                    </template>
                </tbody>
            </table>
        </div>
    </div>

    <div v-if="state.total === null || state.answers.length < state.total" class="form-group m-sm-b hidden-print">
        <button type="button" class="btn btn-default btn-sm" :class="{ 'is-busy': state.isLoading }" :disabled="state.isLoading" @click="loadAnswers">
            {{ state.answers.length === 0 ? $Resources.FreeTextAnswersLoadLatestAnswersBtnText : $Resources.FreeTextAnswersLoadMoreAnswersBtnText }}
        </button>
    </div>
</template>
